import { useTranslation } from 'next-i18next';
import React from 'react';

import { UNFRAMED_IMAGE, WORK_PAGE_TYPE } from '../../../constants';
import { getProductStatus } from '../../../helpers/purchasing';
import { ProductPurchaseDetails } from '../../../types/Purchasing';
import { TrackFramingSwitched } from '../../../utils/analytics';
import { framedByDefault } from '../../../utils/frames';
import { VariantIcon } from '../VariantIcon';
import { ProductVariantGroup } from './ProductVariants';
import { ProductDetailsProps } from './types';

export const ProductFraming = <T extends ProductPurchaseDetails>({
  product: productBlok,
  purchasing,
}: Omit<ProductDetailsProps<T>, 'onShowTaxDetails' | 'onShowFramingDetails'> &
  Partial<
    Pick<ProductDetailsProps<T>, 'onShowTaxDetails' | 'onShowFramingDetails'>
  >) => {
  const { t } = useTranslation('product');
  const {
    checkoutLoading,
    frame,
    frameOptions,
    isFramedByDefault,
    loading,
    product,
    setFrame,
  } = purchasing;

  if (product === null) {
    return null;
  }

  const frameIncluded = framedByDefault(product.framingOffered);

  const artist =
    productBlok.productArtist && productBlok.productArtist.length > 0
      ? (productBlok.productArtist?.[0] as ResolvedRelatedArtist).artist
      : undefined;

  const optionsAvailable = Boolean(frameOptions?.length);

  if (optionsAvailable) {
    return (
      <ProductVariantGroup>
        {/* do not show unframed option if the frame is included */}
        {frameIncluded ? null : (
          <button
            className={`${isFramedByDefault ? 'order-last' : ''}`}
            disabled={loading}
            onClick={() => {
              if (!checkoutLoading) {
                setFrame(null);
                TrackFramingSwitched({
                  page: WORK_PAGE_TYPE,
                  context: 'framing',
                  product: {
                    status: getProductStatus(productBlok, purchasing),
                    artistName: artist ? artist.name : '',
                    editionSize: productBlok.editionSize
                      ? parseInt(productBlok.editionSize, 10)
                      : 0,
                    id: productBlok.id[0].id,
                    releaseType: productBlok.releaseType,
                    releaseDate: productBlok.launchDate,
                    title: productBlok.title,
                    type: 'Product',
                    medium: productBlok.medium
                      ? productBlok.medium[0]
                      : undefined,
                    price: product.price.toString(),
                    currencyCode: product.currency,
                  },
                  frameSelected: false,
                });
              }
            }}
            type="button"
          >
            <VariantIcon
              active={!frame}
              thumbnail={UNFRAMED_IMAGE}
              tooltipText={t('framing.tooltips.unframed')}
            />
          </button>
        )}

        {frameOptions &&
          frameOptions.map((frameOption) => (
            <button
              key={frameOption.title}
              disabled={loading}
              onClick={() => {
                if (!checkoutLoading) {
                  const { variants: submitVariants, ...selectedFramesRest } =
                    frameOption;
                  setFrame({
                    ...selectedFramesRest,
                    variant: submitVariants[0],
                  });
                  TrackFramingSwitched({
                    page: WORK_PAGE_TYPE,
                    context: 'framing',
                    product: {
                      status: getProductStatus(productBlok, purchasing),
                      artistName: artist ? artist.name : '',
                      editionSize: productBlok.editionSize
                        ? parseInt(productBlok.editionSize, 10)
                        : 0,
                      id: productBlok.id[0].id,
                      releaseType: productBlok.releaseType,
                      releaseDate: productBlok.launchDate,
                      title: productBlok.title,
                      type: 'Product',
                      medium: productBlok.medium
                        ? productBlok.medium[0]
                        : undefined,
                      price: product.price.toString(),
                      currencyCode: product.currency,
                    },
                    frameSelected: true,
                    frameName: frameOption.title,
                  });
                }
              }}
              type="button"
            >
              <VariantIcon
                active={frame?.title === frameOption.title}
                text="F"
                thumbnail={frameOption.images[0].src}
                tooltipText={t(`${frameOption.title}`)}
              />
            </button>
          ))}
      </ProductVariantGroup>
    );
  }

  return null;
};
